import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  attachPolicyFiles,
  decidePolicyComment,
  policiesPageQuery,
  policyContentQuery,
  policyListQuery,
  policyNis2Query,
  removePolicyAttachment,
  savePolicyNis2,
  savePolicyText,
  type PoliciesPage,
  type PolicyCategory,
  type PolicyComment,
  type PolicyRow,
  type PolicyTextField,
} from '../api/easeedeskPolicies'
import { esc } from '../cmdb/cells'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { PolicyAcksPanel } from './PolicyAcksPanel'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { PolicyCategoryAccessButton } from './PolicyCategoryAccess'

/**
 * "IT Policies" - `Easeedesk::it_policies`, macheta
 * `views/version-3/policies/index.php`.
 *
 * Doua bucati: coada de observatii (numai pentru cine poate hotari) si lista
 * politicilor, cu aceleasi cinci coloane din `policiesModel` - deschizatorul,
 * Subcategory, Category, Attachment si NIS2. Filtrul de categorie a urcat in
 * antetul paginii, langa cautare.
 *
 * Coada e DESCHISA din start si arata numai ce asteapta: managerul intra pe
 * ecran ca sa lucreze, iar ce s-a hotarat se citeste langa politica. Dupa
 * Approve sau Reject randul pleaca de acolo - altfel coada creste la nesfarsit
 * si peste o luna nu se mai vede ce e de facut printre cele o suta hotarate.
 *
 * ---------------------------------------------------------------------------
 * LISTA E O LISTA, CA ORICARE ALTA DIN MODUL
 * ---------------------------------------------------------------------------
 *
 * „filtre search sus, tabelul full screen si cu headerul tabelului culoarea
 * easeedesk". Tabelul era scris de mana - un `<table class="table
 * table-striped ...">` fara `dataTableM` - iar selectul de categorie si casuta
 * de cautare stateau intr-un rand deasupra lui, cu paginarea proprie atarnand
 * dedesubt. Adica exact ce s-a reparat la catalogul de servicii si la registrul
 * de certificate (11 septembrie 2026).
 *
 * In portal ecranul NU e asa. `policies.js` il porneste ca DataTable pe tabelul
 * `dataTableM`, cu `order: [[1, 'asc']]`, `stateSave`, `serverSide` si
 * `DataTablesProperties.buttons`, si isi MUTA singur selectul de categorie in
 * bara de filtre (`TableActions.initFilters`). Apoi trece `list_chrome.js`:
 * cautarea si filtrele urca in antet, Copy/Columns/Refresh coboara in subsol,
 * iar `<body>` primeste `tk-list-flush`.
 *
 * `DataTable` din v4 e chiar forma aia, deja asezata. Cele trei lucruri cerute
 * vin toate de acolo, si niciunul nu se scrie aici:
 *
 *   - CAUTAREA e casuta din antetul paginii, legata de `state.search` - acelasi
 *     camp pe care il trimitea la server si cea din pagina. A ramas una
 *     singura, sus.
 *   - CATEGORIA urca langa ea, cu `tk-lift-to-header tk-header-select` - locul
 *     pe care `list_chrome.css` il tine pentru alegerea proprie a unui ecran
 *     (vezi `CertificatesPage`). Optiunea goala isi spune singura ce e („All
 *     categories"): in antet nu mai are cine sa-i puna eticheta.
 *   - TABELUL se lipeste de margini fiindca deasupra lui nu mai sta nimic:
 *     `DataTable` pune `tk-list-flush`, si nu e niciun cartonas `adm-page-head`
 *     care sa ceara inapoi cei 20px (`adm.css`). Ca `/app/cmdb`.
 *   - CULOAREA CAPULUI nu se scrie nicaieri: `dataTableM` pe tabel (scris de
 *     `DataTable`) plus clasa modulului pe `<body>` (scrisa de `Shell`) cad
 *     amandoua pe `.easeedesk .dataTableM > thead` din `app.css`. O culoare
 *     scrisa de mana aici ar fi a douasprezecea copie a aceleiasi valori.
 *
 * COADA DE OBSERVATII ramane singurul lucru de deasupra tabelului, si numai
 * cand are ceva in ea: nu e un filtru al listei, ci treaba de facut. Pentru
 * toti ceilalti - si pentru manager in zilele in care coada e goala - deasupra
 * tabelului nu sta nimic, deci pagina se lipeste sus, ca `/app/cmdb`.
 *
 * SORTAREA, PAGINAREA SI CAUTAREA merg la server, ca in portal
 * (`serverSide: true` in `policies.js`). Capul de tabel poate acum cere oricare
 * din cele trei coloane pe care le stie `DbalPoliciesSource::ORDER`, nu doar
 * Subcategory - de-aia `policyListQuery` primeste si numarul coloanei.
 *
 * CE NU S-A MUTAT: SCRISUL. Cele trei casete ale politicii si textul NIS2 se
 * scriu in portal cu CKEditor, cu jurnalul lor de schimbari; aici randul
 * desfasurat le ARATA, cu fisierele si cu documentul NIS2 de descarcat. Tot
 * in portal raman "Create category", agatarea unui fisier nou si stergerea lui.
 */
export function ItPoliciesPage() {
  const page = useQuery(policiesPageQuery)

  return (
    <Content>
      {/* Rolurile pe categorie - numai pentru cine scrie politicile. */}
      {page.data?.canEdit && (
        <div className="aright" style={{ marginBottom: 10 }}>
          <PolicyCategoryAccessButton />
        </div>
      )}
      <PoliciesTable page={page.data} />
    </Content>
  )
}

const TABLE_ID = 'policies-details'
const PAGE = 10

/** Coloanele din `policiesModel`, in aceeasi ordine si cu aceleasi capete. */
function policyColumns(): TableColumn[] {
  const as = (row: Record<string, unknown>) => row as unknown as PolicyRow

  return [
    {
      /* Sageata de deschidere. `DataTable` se leaga de `center.show_details`,
         exact ca `ExpandRow` din `general.js`. */
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 40,
      className: 'text-center',
      html: (row) =>
        '<center class="show_details color-dark-grey pointer font-16" data-id="'
        + as(row).ID + '"><i class="fas fa-plus-circle"></i></center>',
    },
    {
      index: 1,
      title: 'Subcategory',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(as(row).subcategory),
    },
    {
      index: 2,
      title: 'Category',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(as(row).category),
    },
    {
      index: 3,
      title: 'Attachment',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        '<i style="color:dodgerblue;font-size:15px" class="fas fa-file-alt"></i> '
        + as(row).attachment + ' Files attached',
    },
    {
      /* NIS2 nu e in harta de sortare a serverului (`DbalPoliciesSource::ORDER`
         se opreste la 3), deci capul ei nu se apasa: un antet pe care se apasa
         fara sa se schimbe nimic e mai rau decat unul pe care nu se apasa. */
      index: 4,
      title: 'NIS2',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) =>
        as(row).has_nis2
          ? '<a style="color:dodgerblue" href="' + esc(as(row).nis2_url)
            + '"><i style="color:dodgerblue;font-size:15px" class="fas fa-download"></i>'
            + ' Download document</a>'
          : '',
    },
  ]
}

/** Culoarea starii: portocaliu in asteptare, verde aprobat, rosu respins. */
const TONE: Record<number, string> = { 1: '#f0ad4e', 2: '#359c45', 3: '#d83430' }

function CommentsInbox({ comments }: { comments: PolicyComment[] }) {
  const [open, setOpen] = useState(true)
  const pending = comments.filter((one) => one.status === 1).length

  return (
    <div className="policy-inbox" style={{ marginBottom: '16px', border: '1px solid #e4e9ee', borderRadius: '6px', background: '#fff' }}>
      <div
        style={{ padding: '12px 14px', borderBottom: '1px solid #eef2f6', cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setOpen(!open)
        }}
      >
        <strong style={{ fontSize: '13px' }}>
          <i className="fas fa-comment-dots" style={{ color: '#f0ad4e' }}></i> Comments on policies
          <i className={`fas ${open ? 'fa-angle-up' : 'fa-angle-down'} pull-right`}></i>
        </strong>
        {pending > 0 ? (
          <span
            style={{
              marginLeft: '6px',
              padding: '1px 9px',
              borderRadius: '10px',
              background: '#f0ad4e',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            {pending} waiting
          </span>
        ) : (
          <span className="text-muted" style={{ marginLeft: '6px', fontSize: '12px' }}>
            nothing waiting
          </span>
        )}
      </div>

      {open && (
        <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
          {comments.map((one) => (
            <CommentRow one={one} key={one.id} />
          ))}
        </div>
      )}
    </div>
  )
}

function CommentRow({ one }: { one: PolicyComment }) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const client = useQueryClient()
  const color = TONE[one.status] ?? '#8a949e'

  async function decide(status: number) {
    setBusy(true)
    try {
      const out = await decidePolicyComment(one.id, status, note.trim())
      if (!out.ok) {
        setBusy(false)
        toast(out.message ?? 'Not saved', 'Error', 'error')
        return
      }
      toast(out.message ?? 'Decision saved.', 'Saved', 'success')
      /* Hotararea schimba si pastila de sus, si randul, si ordinea listei:
         se cere pagina din nou, in loc sa se petice trei locuri deodata. */
      void client.invalidateQueries({ queryKey: ['easeedesk', 'it-policies'] })
    } catch (err) {
      setBusy(false)
      toast(err instanceof Error ? err.message : 'Not saved', 'Error', 'error')
    }
  }

  return (
    <div className="pc-row" style={{ padding: '11px 14px', borderBottom: '1px solid #f4f7f9', borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: '11px', color: '#98a1aa' }}>
        <strong style={{ color: '#3d444b' }}>{one.author_name}</strong>
        {' · '}
        {one.created}
        {one.policy_name ? ` · ${one.policy_name}` : ''}
        {one.ticket_id > 0 && (
          <>
            {' · '}
            <a href={`/app/easeedesk/ticket/${one.ticket_id}`} target="_blank" rel="noopener noreferrer">
              ticket #{one.ticket_id}
            </a>
          </>
        )}
        <span style={{ float: 'right', color, fontWeight: 600 }}>{one.state}</span>
      </div>

      <div style={{ marginTop: '4px', whiteSpace: 'pre-line', color: '#3d444b', fontSize: '13px' }}>{one.comment}</div>

      {one.status === 1 ? (
        <div style={{ marginTop: '8px' }}>
          <input
            type="text"
            className="form-control input-sm pc-note"
            style={{ display: 'inline-block', width: '340px', marginRight: '6px' }}
            placeholder="Why (optional, but it is what the author reads)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button type="button" className="btn btn-success btn-sm pc-decide" disabled={busy} onClick={() => void decide(2)}>
            <i className="fas fa-check"></i> Approve
          </button>{' '}
          <button type="button" className="btn btn-default btn-sm pc-decide" disabled={busy} onClick={() => void decide(3)}>
            <i className="fas fa-times"></i> Reject
          </button>
        </div>
      ) : (
        one.decided_by_name !== '' && (
          <div style={{ marginTop: '6px', fontSize: '11px', color: '#98a1aa' }}>
            {one.state} by {one.decided_by_name}
            {one.decision_note ? ` — ${one.decision_note}` : ''}
          </div>
        )
      )}
    </div>
  )
}

function PoliciesTable({ page }: { page?: PoliciesPage }) {
  const [category, setCategory] = useState(0)
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: PAGE,
    /* `order: [[1, 'asc']]` si zece randuri pe pagina, ca in `policies.js`;
       `useTableState` tine minte alegerea, ca `stateSave`. */
    order: { column: 1, dir: 'asc' },
    search: '',
  })

  const list = useQuery({
    ...policyListQuery(state.search, category, state.start, state.length, state.order.column, state.order.dir),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo(() => policyColumns(), [])
  const categories: PolicyCategory[] = page?.categories ?? []

  return (
    <DataTable
      id={TABLE_ID}
      columnTools
      columns={columns}
      rows={(list.data?.data ?? []) as unknown as Record<string, unknown>[]}
      total={list.data?.recordsFiltered ?? 0}
      fetching={list.isFetching}
      error={list.error ? (list.error as Error).message : null}
      onRetry={() => void list.refetch()}
      state={state}
      onState={setState}
      rowId={(row) => `policy_${(row as unknown as PolicyRow).ID}`}
      searchHint="subcategory name"
      emptyHint="No policy yet - one per ticket subcategory."
      details={(row) => <PolicyTabs subcategory={(row as unknown as PolicyRow).ID} />}
      /*
       * CATEGORIA, in bara de sus, langa cautare.
       *
       * In portal selectul asta nici nu sta in pagina: `TableActions.initFilters`
       * il muta in bara de filtre a tabelului, si `list_chrome.js` il urca de
       * acolo in antet. `tk-lift-to-header tk-header-select` e chiar locul ala -
       * unde stau si selectul lui Discovered, si cel al registrului de
       * certificate. Aici nu exista list_chrome.js, deci clasa o scrie pagina.
       *
       * Optiunea goala isi spune singura ce e: in antet nu mai are cine sa-i
       * puna eticheta „Category".
       */
      headerView={
        <div className="tk-lift-to-header tk-header-select" style={{ display: 'inline-block', textAlign: 'left' }}>
          <select
            className="form-control input-sm"
            id="category-filter"
            title="Category"
            aria-label="Category"
            value={category}
            onChange={(e) => {
              setCategory(Number(e.target.value))
              setState({ start: 0 })
            }}
          >
            <option value={0}>All categories</option>
            {categories.map((one) => (
              <option value={one.ID} key={one.ID}>
                {one.category_name}
              </option>
            ))}
          </select>
        </div>
      }
      /* Copy / Columns / Refresh - `DataTablesProperties.buttons` din macheta
         veche, pe care list_chrome le coboara in subsol. */
      footerButtons={{ copy: true, colvis: true, refresh: () => void list.refetch() }}
    >
      {/*
       * COADA DE OBSERVATII ramane deasupra tabelului, si e singurul lucru care
       * are voie sa stea acolo: nu e un filtru al listei, ci treaba de facut -
       * managerul intra pe ecran ca s-o lucreze. Se vede numai pentru cine poate
       * hotari SI numai cand chiar are ceva in ea, deci in mod obisnuit deasupra
       * tabelului nu sta nimic si pagina e lipita sus, ca `/app/cmdb`.
       *
       * Marginea si-o pune singura: `tk-list-flush` scoate spatierea paginii,
       * iar un cartonas lipit de marginea ferestrei arata ca o greseala.
       */}
      {page?.canDecide && page.comments.length > 0 && (
        <div style={{ padding: '16px 20px 0' }}>
          <CommentsInbox comments={page.comments} />
        </div>
      )}
    </DataTable>
  )
}

/**
 * Cele trei file din `tab_list.php`: Service Provider, Company, NIS2.
 *
 * FILELE SUNT ANCORE, nu butoane. Meniul din stanga randului desfacut se
 * imbraca din `app.css` cu `li a.action_button` - selectorul cere un `<a>`, si
 * un `<button>` cu aceeasi clasa ramane un buton gri de browser, langa el.
 * Asa a iesit prima data („pe expand nu e meniul ca in celelalte"). Toate
 * celelalte liste cu rand desfacut - CMDB, catalogul de servicii, licentele -
 * scriu ancore, cu adresa filei din portal in `href` si apasarea oprita, si
 * exact asa scrie si macheta veche.
 */
function PolicyTabs({ subcategory }: { subcategory: number }) {
  const [tab, setTab] = useState<PolicyTab>('provider')
  const tabs: [PolicyTab, string, string][] = [
    ['provider', 'Service Provider', `/policies/getPoliciesContent/${subcategory}/1`],
    ['company', 'Company', `/policies/getPoliciesContent/${subcategory}/2`],
    ['nis2', 'NIS2', `/policies/getNis2Content/${subcategory}`],
    ['acks', 'Confirmations', `/easeedesk/it_policies#acks-${subcategory}`],
  ]

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.map(([name, label, href]) => (
            <li key={name}>
              <a
                href={href}
                className={tab === name ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(name)
                }}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'nis2' ? (
            <Nis2Panel subcategory={subcategory} />
          ) : tab === 'acks' ? (
            <PolicyAcksPanel subcategory={subcategory} />
          ) : (
            <ContentPanel subcategory={subcategory} scope={tab} />
          )}
        </div>
      </div>
    </div>
  )
}

type PolicyTab = 'provider' | 'company' | 'nis2' | 'acks'

function ContentPanel({ subcategory, scope }: { subcategory: number; scope: 'provider' | 'company' }) {
  const q = useQuery(policyContentQuery(subcategory, scope))
  const page = useQuery(policiesPageQuery)
  const client = useQueryClient()
  const data = q.data
  const canEdit = page.data?.canEdit ?? false
  const canDelete = page.data?.canDelete ?? false

  if (!data) return null

  const reload = () => void client.invalidateQueries({ queryKey: ['easeedesk', 'it-policies', subcategory] })

  const save = (field: PolicyTextField, html: string) =>
    savePolicyText(subcategory, scope, field, html)
      .then(() => {
        toast('Saved.', 'Policy', 'success')
        reload()
      })
      .catch((err: unknown) => {
        toast(err instanceof Error ? err.message : 'Not saved', 'Error', 'error')
      })

  return (
    <div className="row">
      <div className="col-md-6">
        <PolicyBlock title="Standardized Operation" html={data.standard} canEdit={canEdit} onSave={(html) => save('standard_details', html)} />
        <PolicyBlock title="Configuration Details" html={data.configuration} canEdit={canEdit} onSave={(html) => save('configuration_details', html)} top />
        <PolicyBlock title="Exceptions" html={data.exception} canEdit={canEdit} onSave={(html) => save('exception_details', html)} top />
      </div>

      <div className="col-md-6">
        <h4 className="mb-2">
          <b>Attachments</b>
        </h4>
        <table className="table table-hover">
          <tbody>
            {data.attachments.length === 0 ? (
              <tr>
                <td colSpan={2}>Sorry, nothing to see yet.</td>
              </tr>
            ) : (
              data.attachments.map((file) => (
                <tr key={file.id}>
                  <td>
                    <i style={{ color: 'dodgerblue', fontSize: '15px' }} className="fas fa-file-alt me-2"></i> {file.file_name}
                  </td>
                  <td>
                    <a href={file.url || undefined}>
                      <button type="button" style={{ width: '70px' }} className="btn btn-success btn-xs" title="Download file">
                        <i className="fa fa-download" aria-hidden="true"></i>
                      </button>
                    </a>
                    {canDelete && (
                      <button
                        type="button"
                        style={{ width: '70px', marginLeft: 4 }}
                        className="btn btn-danger btn-xs delete-attachment"
                        title="Delete file"
                        onClick={() => {
                          if (!window.confirm('Are you sure you want to delete this attachment?')) return
                          removePolicyAttachment(file.id)
                            .then((reply) => {
                              toast(reply.message, 'Policy', 'success')
                              reload()
                            })
                            .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Not removed', 'Error', 'error'))
                        }}
                      >
                        <i className="fa fa-trash" aria-hidden="true"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {canEdit && <PolicyUpload subcategory={subcategory} scope={scope} onDone={reload} />}
      </div>
    </div>
  )
}

/**
 * Un text al politicii, cu "Save" cand omul are dreptul - cele trei formulare
 * din `policies_details_content.php`, fiecare cu campul lui. Fara CKEditor:
 * v4 nu il are, HTML-ul se scrie brut in casuta.
 */
function PolicyBlock({ title, html, canEdit, onSave, top = false }: { title: string; html: string; canEdit: boolean; onSave(html: string): Promise<void>; top?: boolean }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(html)
  const [busy, setBusy] = useState(false)

  return (
    <div>
      <h4 className="mb-2" style={top ? { marginTop: '30px' } : undefined}>
        <b>{title}</b>
        {canEdit && !editing && (
          <button
            type="button"
            className="btn btn-default btn-xs pull-right"
            onClick={() => {
              setDraft(html)
              setEditing(true)
            }}
          >
            <i className="fa fa-pencil-alt" aria-hidden="true"></i> Edit
          </button>
        )}
      </h4>
      {editing ? (
        <form
          className="policy-edit"
          onSubmit={(ev) => {
            ev.preventDefault()
            setBusy(true)
            void onSave(draft).finally(() => {
              setBusy(false)
              setEditing(false)
            })
          }}
        >
          <textarea className="form-control input-sm standard-editor" rows={8} value={draft} onChange={(ev) => setDraft(ev.target.value)}></textarea>
          <div className="aright" style={{ marginTop: 6 }}>
            <button type="button" className="btn btn-default btn-sm" onClick={() => setEditing(false)}>
              Cancel
            </button>{' '}
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              Save
            </button>
          </div>
        </form>
      ) : (
        <PolicyText html={html} />
      )}
    </div>
  )
}

/** `#formUpload`: `filesToUpload[]` + "Save attachments". */
function PolicyUpload({ subcategory, scope, onDone }: { subcategory: number; scope: 'provider' | 'company'; onDone(): void }) {
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)

  return (
    <form
      id="formUpload"
      onSubmit={(ev) => {
        ev.preventDefault()
        if (files.length === 0) return
        setBusy(true)
        attachPolicyFiles(subcategory, scope, files)
          .then((reply) => {
            toast(reply.message, 'Policy', 'success')
            setFiles([])
            onDone()
          })
          .catch((err: unknown) => toast(err instanceof Error ? err.message : 'File could not be uploaded! Please try again..', 'Error', 'error'))
          .finally(() => setBusy(false))
      }}
    >
      <input type="file" name="filesToUpload[]" id="filesToUpload" multiple onChange={(ev) => setFiles(Array.from(ev.target.files ?? []))} />
      <button type="submit" id="submitUpload" className="btn btn-default btn-primary btn-sm pull-right" disabled={busy || files.length === 0}>
        Save attachments
      </button>
    </form>
  )
}

/**
 * Un camp de politica fara text scrie „n/a”, nu ramane o eticheta cu nimic
 * sub ea; cine poate scrie apasa "Edit" si primeste casuta.
 */
function PolicyText({ html }: { html: string }) {
  if (html.replace(/<[^>]*>|&nbsp;/g, '').trim() === '') {
    return <p className="text-muted policy-empty">n/a</p>
  }
  return <div dangerouslySetInnerHTML={{ __html: html }} />
}

function Nis2Panel({ subcategory }: { subcategory: number }) {
  const q = useQuery(policyNis2Query(subcategory))
  const client = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const data = q.data

  if (!data) return null

  return (
    <div>
      <div className="aright" style={{ marginBottom: '8px' }}>
        <a href={data.download} className="btn btn-default btn-sm">
          <i className="fa fa-download" aria-hidden="true"></i> Download document
        </a>
        {data.canEdit && !editing && (
          <>
            {' '}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setDraft(data.nis2)
                setEditing(true)
              }}
            >
              <i className="fa fa-pencil-alt" aria-hidden="true"></i> Edit
            </button>
          </>
        )}
      </div>
      {editing ? (
        <form
          id="editNis2Form"
          onSubmit={(ev) => {
            ev.preventDefault()
            setBusy(true)
            savePolicyNis2(subcategory, draft)
              .then(() => {
                toast('Saved.', 'NIS2', 'success')
                void client.invalidateQueries({ queryKey: ['easeedesk', 'it-policies', subcategory] })
                setEditing(false)
              })
              .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Not saved', 'Error', 'error'))
              .finally(() => setBusy(false))
          }}
        >
          <textarea className="form-control input-sm standard-editor" name="nis2_content" rows={14} value={draft} onChange={(ev) => setDraft(ev.target.value)}></textarea>
          <div className="aright" style={{ marginTop: 6 }}>
            <button type="button" className="btn btn-default btn-sm" onClick={() => setEditing(false)}>
              Cancel
            </button>{' '}
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              Save
            </button>
          </div>
        </form>
      ) : (
        <div dangerouslySetInnerHTML={{ __html: data.nis2 }} />
      )}
    </div>
  )
}
